import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TourneeHealthCard from "../../../components/tournees/TourneeHealthCard";
import ExecutionStatusBadge from "../../../modules/execution/components/ExecutionStatusBadge";
import { useTourneeLiveExecution } from "../../../modules/execution/hooks/useTourneeLiveExecution";
import { useExecutionMutations } from "../../../modules/execution/services/executionMutations";
import { fetchTourneeClosurePreview } from "../../../modules/execution/services/execution.service";
import type { PassageIncidentType, TourneeClosurePreview } from "../../../modules/execution/types/execution.types";
import { frGpsStatus } from "../../../modules/execution/utils/qualityLabels";
import { api } from "../../../lib/axios";

function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}h ${String(m).padStart(2, "0")}m`;
}

const SEVERITY_LABEL_FR: Record<string, string> = {
  OK: "Conforme",
  INFO: "Information",
  WARNING: "Avertissement",
  CRITICAL: "Critique",
  BLOCKING: "Bloquant",
};

function closureModalTitle(sev: TourneeClosurePreview["severity"] | undefined): string {
  switch (sev) {
    case "BLOCKING":
      return "Clôture impossible";
    case "CRITICAL":
      return "Clôture à risque — motif obligatoire";
    case "WARNING":
      return "Clôture possible avec avertissements";
    case "INFO":
      return "Clôture — informations";
    default:
      return "Clôturer cette tournée ?";
  }
}

const ROW_CLASS_BY_STATUS: Record<string, string> = {
  EN_COURS: "border-emerald-400 bg-emerald-800/20 shadow-[0_0_0_1px_rgba(16,185,129,0.35)]",
  REALISE: "border-emerald-700/40 bg-emerald-950/20",
  ECHEC: "border-red-600/40 bg-red-950/20",
  PLANIFIE: "border-slate-700 bg-slate-900/40",
};

export default function TourneeExecutionPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, refresh } = useTourneeLiveExecution(id);
  const mutations = useExecutionMutations({ tourneeId: id, refresh });

  const [incidentTargetPassageId, setIncidentTargetPassageId] = useState<number | null>(null);
  const [viewIncidentPassageId, setViewIncidentPassageId] = useState<number | null>(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [closurePreview, setClosurePreview] = useState<TourneeClosurePreview | null>(null);
  const [closurePreviewLoading, setClosurePreviewLoading] = useState(false);
  const [closureReason, setClosureReason] = useState("");
  const [incidentType, setIncidentType] = useState<PassageIncidentType>("OTHER");
  const [incidentDescription, setIncidentDescription] = useState("");
  const [nowMs, setNowMs] = useState(Date.now());

  const [governanceEvents, setGovernanceEvents] = useState<
    Array<{ id: number; action: string; label: string | null; createdAt: string; actorName: string | null }>
  >([]);

  useEffect(() => {
    const handle = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(handle);
  }, []);

  useEffect(() => {
    if (!id) return;
    void api
      .get<{ success?: boolean; items?: Array<{ id: number; action: string; label: string | null; createdAt: string; actorName: string | null }> }>(
        `/workflow/timeline/TOURNEE/${id}`
      )
      .then((res) => {
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        setGovernanceEvents(items);
      })
      .catch(() => setGovernanceEvents([]));
  }, [id]);

  useEffect(() => {
    if (!confirmCloseOpen || !id) return;
    setClosurePreviewLoading(true);
    setClosurePreview(null);
    setClosureReason("");
    void fetchTourneeClosurePreview(id)
      .then((p) => setClosurePreview(p))
      .catch(() => setClosurePreview(null))
      .finally(() => setClosurePreviewLoading(false));
  }, [confirmCloseOpen, id]);

  const incidentTypeOptions = useMemo(
    () => [
      { value: "ACCESS_DENIED" as PassageIncidentType, label: "Accès refusé" },
      { value: "CLIENT_ABSENT" as PassageIncidentType, label: "Client absent" },
      { value: "ZONE_INACCESSIBLE" as PassageIncidentType, label: "Zone inaccessible" },
      { value: "VEHICLE_ISSUE" as PassageIncidentType, label: "Problème véhicule" },
      { value: "SECURITY_ISSUE" as PassageIncidentType, label: "Sécurité" },
      { value: "OTHER" as PassageIncidentType, label: "Autre" },
    ],
    []
  );

  const hasRunningPassage = useMemo(
    () => data?.tournee?.passages?.some((it) => it.executionStatus === "EN_COURS") ?? false,
    [data?.tournee?.passages]
  );

  const progress = useMemo(() => {
    if (!data) return 0;
    return Math.max(0, Math.min(100, Math.round(data.health.progressPercent ?? data.tournee.completionPercent ?? 0)));
  }, [data]);

  const canStartPlannedPassage = useMemo(
    () => data?.tournee?.executionStatus === "EN_COURS" && !hasRunningPassage,
    [data?.tournee?.executionStatus, hasRunningPassage]
  );

  const canCloseTournee = useMemo(() => {
    if (data?.canCloseTournee != null) return Boolean(data.canCloseTournee);
    return data?.tournee?.executionStatus === "EN_COURS" && !hasRunningPassage;
  }, [data?.canCloseTournee, data?.tournee?.executionStatus, hasRunningPassage]);

  const readyToCloseHint = useMemo(() => {
    if (!data?.tournee || data.tournee.executionStatus !== "EN_COURS") return null;
    if (progress === 100 && (data.pending ?? 0) === 0 && !hasRunningPassage) {
      const warns = data.closeWarnings ?? [];
      if (warns.length > 0) return "Clôture possible avec avertissements — vérifiez le résumé.";
      return "Tournée prête à clôture.";
    }
    return null;
  }, [data, progress, hasRunningPassage]);

  const readOnly = data?.tournee?.executionStatus === "TERMINEE";
  const canUsePassageActions = data?.tournee?.executionStatus === "EN_COURS";

  const timelineRows = useMemo(() => {
    if (!data?.tournee?.passages?.length) return [];
    return data.tournee.passages.map((it, idx) => {
      const planned = new Date(it.datePrevue).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
      const start = it.startedAt
        ? new Date(it.startedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
        : null;
      const end = it.completedAt
        ? new Date(it.completedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
        : null;
      const duration =
        it.executionDurationMin != null
          ? `${it.executionDurationMin} min`
          : it.startedAt
            ? `${Math.max(0, Math.round((nowMs - new Date(it.startedAt).getTime()) / 60000))} min`
            : null;
      return { it, idx, planned, start, end, duration };
    });
  }, [data?.tournee?.passages, nowMs]);

  const currentElapsed = useMemo(() => {
    const currentPassage = data?.currentPassage;
    if (!currentPassage?.startedAt) return null;
    const minutes = Math.max(
      0,
      Math.round((nowMs - new Date(currentPassage.startedAt).getTime()) / 60000)
    );
    return formatDuration(minutes);
  }, [data?.currentPassage, nowMs]);

  const incidentsForViewedPassage = useMemo(
    () => (data?.incidents ?? []).filter((i) => i.passageId === viewIncidentPassageId),
    [data?.incidents, viewIncidentPassageId]
  );

  const disabledReasonForPassageActions = useMemo(() => {
    const st = data?.tournee?.executionStatus;
    if (st === "PLANIFIEE") return "Lancez la tournée avant de démarrer un passage.";
    if (st === "SUSPENDUE") return "Tournée suspendue.";
    if (st === "TERMINEE") return "Lecture seule — tournée terminée.";
    return "";
  }, [data?.tournee?.executionStatus]);

  const submitIncident = async () => {
    if (!incidentTargetPassageId) return;
    if (incidentDescription.trim().length < 5) {
      window.alert("Description obligatoire (minimum 5 caractères).");
      return;
    }
    const ok = await mutations.reportIncident(incidentTargetPassageId, {
      type: incidentType,
      description: incidentDescription.trim(),
    });
    if (!ok) return;
    setIncidentTargetPassageId(null);
    setIncidentDescription("");
    setIncidentType("OTHER");
  };

  const backToTournees = (
    <div className="mb-4">
      <Link
        to="/admin/planificateur/tournees"
        className="inline-flex items-center gap-2 rounded-xl border border-slate-600/90 bg-slate-950/70 px-3 py-2 text-sm font-semibold text-slate-200 shadow-sm transition hover:border-slate-500 hover:bg-slate-900 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Retour aux tournées
      </Link>
    </div>
  );

  if (loading && !data) {
    return (
      <div className="p-4 text-slate-300 md:p-6">
        {backToTournees}
        <p>Chargement centre d’exécution...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-4 md:p-6">
        {backToTournees}
        <div className="rounded-xl border border-amber-600/40 bg-amber-900/20 p-4 text-amber-100">
          <p className="font-semibold">Connexion instable. Réessayez.</p>
          <button
            onClick={() => void refresh()}
            className="mt-3 rounded border border-amber-400/50 px-4 py-2 text-sm font-semibold"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-4 text-slate-300 md:p-6">
        {backToTournees}
        <p>Aucune donnée.</p>
      </div>
    );
  }

  const current = data.currentPassage;

  return (
    <div className="space-y-5 p-4 text-slate-100 md:p-6">
      {backToTournees}

      <header className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 md:p-5">
        <p className="text-xs uppercase tracking-wider text-slate-400">CENTRE D&apos;EXÉCUTION TERRAIN</p>
        <h1 className="text-xl font-bold text-white md:text-2xl">{data.tournee.numero ?? `Tournée #${data.tournee.id}`}</h1>
        <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-200 md:grid-cols-3">
          <p>Équipe: {data.tournee.equipe?.nom ?? "—"}</p>
          <p>Mode opérationnel: {data.tournee.vehicule ? "CAMION/MOTO" : "PIÉTON"}</p>
          <p>Chauffeur: {[data.tournee.chauffeur?.prenom, data.tournee.chauffeur?.nom].filter(Boolean).join(" ") || "—"}</p>
          <p>Statut: <ExecutionStatusBadge value={data.tournee.executionStatus} /></p>
          <p>Progression: {progress}%</p>
          <p>Zone: {data.tournee.zone?.nom ?? "—"}</p>
        </div>
        <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-800">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 text-xs md:grid-cols-5">
          <span className="rounded bg-slate-950/60 px-2 py-1">Réalisés: {data.completed}</span>
          <span className="rounded bg-slate-950/60 px-2 py-1">Restants: {data.pending}</span>
          <span className="rounded bg-slate-950/60 px-2 py-1">Incidents: {data.incidents.length}</span>
          <span className="rounded bg-slate-950/60 px-2 py-1">Progression: {progress}%</span>
          <span className="rounded bg-slate-950/60 px-2 py-1">{hasRunningPassage ? "Oui" : "Non"} passage en cours</span>
        </div>
      </header>

      {readyToCloseHint ? (
        <div className="rounded-xl border border-blue-500/40 bg-blue-950/30 px-4 py-3 text-sm text-blue-100">
          {readyToCloseHint}
        </div>
      ) : null}

      <section>
        <TourneeHealthCard health={data.health} />
      </section>

      <section className="rounded-2xl border border-slate-700 bg-slate-900/30 p-4 text-sm text-slate-200 md:p-5">
        <h2 className="text-base font-semibold text-white">Journal opérationnel</h2>
        <p className="mt-1 text-xs text-slate-400">
          Événements métier (workflow / gouvernance). Source API{" "}
          <span className="font-mono text-slate-300">GET /api/workflow/timeline/TOURNEE/:id</span>.
        </p>
        {governanceEvents.length === 0 ? (
          <p className="mt-3 text-slate-500">Aucun événement enregistré ou chargement indisponible.</p>
        ) : (
          <ul className="mt-3 list-inside list-disc space-y-2 text-slate-300">
            {governanceEvents.slice(0, 40).map((ev) => (
              <li key={ev.id}>
                <span className="font-mono text-xs text-slate-400">{ev.action}</span>
                {ev.label ? ` — ${ev.label}` : ""}
                {ev.actorName ? ` · ${ev.actorName}` : ""}
                <span className="text-slate-500">
                  {" "}
                  ({new Date(ev.createdAt).toLocaleString("fr-FR")})
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-2xl border border-emerald-700/40 bg-emerald-900/10 p-4 shadow-[0_0_30px_rgba(16,185,129,0.12)] md:p-5">
        <h2 className="text-lg font-semibold text-white">Passage courant</h2>
        {current ? (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-1 gap-2 text-sm text-slate-100 md:grid-cols-2">
              <p>Client: {current.client?.nomEntreprise || [current.client?.nom, current.client?.prenom].filter(Boolean).join(" ") || "—"}</p>
              <p>
                Priorité:{" "}
                {current.isUrgent
                  ? current.executionStatus === "REALISE"
                    ? "Urgent (traité)"
                    : "Urgent"
                  : "Normale"}
              </p>
              <p>Heure prévue: {new Date(current.datePrevue).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
              <p className="flex flex-wrap items-center gap-2">
                Statut: <ExecutionStatusBadge value={current.executionStatus} />{" "}
                <span className="rounded bg-emerald-600/30 px-2 py-0.5 text-xs text-emerald-200">EN COURS</span>
              </p>
              {current.qualityUi ? (
                <p className="text-xs text-slate-400">
                  GPS: {frGpsStatus(current.qualityUi.gpsStatus, current.qualityUi.gpsReason)} • Photos{" "}
                  {current.qualityUi.photoPresentCount}/{current.qualityUi.photoRequired} •{" "}
                  {current.qualityUi.validationLabel}
                  {current.qualityUi.isFacturable ? " • Facturable" : " • Non facturable"}
                </p>
              ) : null}
              <p className="text-base font-semibold text-emerald-100">Timer: {currentElapsed ?? "00h 00m"}</p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {canUsePassageActions ? (
                <>
                  <button
                    aria-disabled={mutations.pending.completePassage || current.executionStatus !== "EN_COURS"}
                    disabled={mutations.pending.completePassage || current.executionStatus !== "EN_COURS"}
                    title={current.executionStatus !== "EN_COURS" ? "Ce passage n'est pas en cours." : ""}
                    onClick={() => void mutations.completePassage(current.id)}
                    className="min-h-12 rounded border border-blue-500/50 px-4 py-3 text-sm font-semibold text-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {mutations.pending.completePassage ? "..." : "Terminer passage"}
                  </button>
                  <button
                    aria-disabled={mutations.pending.failPassage || current.executionStatus !== "EN_COURS"}
                    disabled={mutations.pending.failPassage || current.executionStatus !== "EN_COURS"}
                    title={current.executionStatus !== "EN_COURS" ? "Ce passage n'est pas en cours." : ""}
                    onClick={() => void mutations.failPassage(current.id)}
                    className="min-h-12 rounded border border-red-500/50 px-4 py-3 text-sm font-semibold text-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {mutations.pending.failPassage ? "..." : "Échec"}
                  </button>
                  <button
                    aria-disabled={mutations.pending.reportIncident}
                    disabled={mutations.pending.reportIncident}
                    onClick={() => setIncidentTargetPassageId(current.id)}
                    className="min-h-12 rounded border border-amber-500/50 px-4 py-3 text-sm font-semibold text-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Incident
                  </button>
                </>
              ) : (
                <p className="text-sm text-slate-300">{disabledReasonForPassageActions}</p>
              )}
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-slate-300">
            {readOnly ? "Exécution terminée. Données disponibles en lecture seule." : "Aucun passage en cours. Démarrez un passage depuis la timeline."}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <div className="mb-3 flex flex-wrap gap-2">
          {data.tournee.executionStatus === "PLANIFIEE" ? (
            <>
              <button
                onClick={() => void mutations.startTournee()}
                disabled={mutations.pending.startTournee}
                className="min-h-12 rounded border border-emerald-500/50 px-4 py-3 text-sm font-semibold text-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutations.pending.startTournee ? "Lancement..." : "Lancer tournée"}
              </button>
              <button
                disabled
                aria-disabled="true"
                title="Lancez la tournée avant de démarrer un passage."
                className="min-h-12 rounded border border-slate-600 px-4 py-3 text-sm text-slate-300 opacity-50 cursor-not-allowed"
              >
                Actions passage désactivées
              </button>
            </>
          ) : null}
          {data.tournee.executionStatus === "EN_COURS" ? (
            <>
              <button
                onClick={() => void mutations.pauseTournee()}
                disabled={mutations.pending.pauseTournee}
                className="min-h-12 rounded border border-amber-500/50 px-4 py-3 text-sm font-semibold text-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutations.pending.pauseTournee ? "Suspension..." : "Suspendre tournée"}
              </button>
              {canCloseTournee ? (
                <button onClick={() => setConfirmCloseOpen(true)} className="min-h-12 rounded border border-blue-500/50 px-4 py-3 text-sm font-semibold text-blue-100">
                  Clôturer tournée
                </button>
              ) : (
                <span className="rounded border border-slate-700 px-3 py-2 text-sm text-slate-400">
                  Impossible de clôturer : un passage est en cours
                </span>
              )}
            </>
          ) : null}
          {data.tournee.executionStatus === "SUSPENDUE" ? (
            <>
              <button
                onClick={() => void mutations.resumeTournee()}
                disabled={mutations.pending.resumeTournee}
                className="min-h-12 rounded border border-emerald-500/50 px-4 py-3 text-sm font-semibold text-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutations.pending.resumeTournee ? "Reprise..." : "Reprendre tournée"}
              </button>
              <button
                disabled
                aria-disabled="true"
                title="Tournée suspendue."
                className="min-h-12 rounded border border-slate-600 px-4 py-3 text-sm text-slate-300 opacity-50 cursor-not-allowed"
              >
                Actions passage désactivées
              </button>
              <span className="rounded border border-amber-700/40 px-3 py-2 text-sm text-amber-300">Exécution suspendue. Reprenez la tournée pour continuer.</span>
            </>
          ) : null}
          {readOnly ? (
            <>
              <button
                disabled
                aria-disabled="true"
                title="Lecture seule — tournée terminée."
                className="min-h-12 rounded border border-slate-600 px-4 py-3 text-sm text-slate-300 opacity-50 cursor-not-allowed"
              >
                Actions exécution désactivées
              </button>
              <span className="rounded border border-blue-700/40 px-3 py-2 text-sm text-blue-300">Lecture seule — tournée terminée.</span>
            </>
          ) : null}
        </div>
        <h2 className="text-lg font-semibold text-white">Timeline</h2>
        {timelineRows.length === 0 ? (
          <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/30 p-4 text-sm text-slate-300">
            Cette tournée ne contient aucun passage.
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {timelineRows.map(({ it, idx, planned, start, end, duration }) => (
              <div
                key={it.id}
                className={`rounded-lg border px-3 py-3 ${ROW_CLASS_BY_STATUS[it.executionStatus] ?? ROW_CLASS_BY_STATUS.PLANIFIE}`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-3 text-sm text-slate-100">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-600 bg-slate-950 text-xs font-bold">#{idx + 1}</span>
                  <div className="h-8 w-px bg-slate-700/70" />
                  <div>
                    <p>{it.client?.nomEntreprise || [it.client?.nom, it.client?.prenom].filter(Boolean).join(" ") || `Passage #${it.id}`}</p>
                    <p className="text-xs text-slate-300">
                      Prévu: {planned}
                      {start ? ` • Début: ${start}` : ""}
                      {end ? ` • Fin: ${end}` : ""}
                      {duration ? ` • Durée: ${duration}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:justify-end">
                  {it.isUrgent ? (
                    <span className="rounded-full border border-red-500/40 px-2 py-0.5 text-xs text-red-200">
                      {it.executionStatus === "REALISE" ? "Urgent — traité" : "Urgent (priorité)"}
                    </span>
                  ) : null}
                  {it.incidentCount > 0 ? (
                    <button
                      onClick={() => setViewIncidentPassageId(it.id)}
                      className="min-h-10 rounded-full border border-amber-500/40 px-3 py-1 text-xs font-semibold text-amber-100"
                    >
                      Voir incidents ({it.incidentCount})
                    </button>
                  ) : null}
                  <ExecutionStatusBadge value={it.executionStatus} />
                  {it.executionStatus === "REALISE" && it.qualityUi ? (
                    <span className="max-w-[220px] truncate text-[10px] text-slate-400" title={it.qualityUi.validationLabel}>
                      {it.qualityUi.validationLabel} •{" "}
                      {frGpsStatus(it.qualityUi.gpsStatus, it.qualityUi.gpsReason)}
                    </span>
                  ) : null}
                  {it.executionStatus === "PLANIFIE" && canStartPlannedPassage ? (
                    <button
                      disabled={mutations.pending.startPassage}
                      onClick={() => void mutations.startPassage(it.id)}
                      className="min-h-10 rounded border border-emerald-500/50 px-3 py-2 text-xs font-semibold text-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {mutations.pending.startPassage ? "..." : "Démarrer"}
                    </button>
                  ) : it.executionStatus === "PLANIFIE" ? (
                    <button
                      disabled
                      title={disabledReasonForPassageActions || "Un autre passage est déjà en cours."}
                      className="min-h-10 rounded border border-slate-600 px-3 py-2 text-xs text-slate-300 opacity-60 cursor-not-allowed"
                    >
                      Démarrer
                    </button>
                  ) : null}
                </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {incidentTargetPassageId != null ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-2xl border border-slate-700 bg-slate-900 p-4 sm:p-5">
            <h3 className="text-lg font-semibold text-white">Signaler un incident</h3>
            <div className="mt-4 space-y-3 overflow-y-auto pr-1">
              <label className="block text-sm text-slate-300">
                Type incident *
                <select
                  value={incidentType}
                  onChange={(e) => setIncidentType(e.target.value as PassageIncidentType)}
                  className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                >
                  {incidentTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm text-slate-300">
                Description *
                <textarea
                  value={incidentDescription}
                  onChange={(e) => setIncidentDescription(e.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                />
              </label>
            </div>
            <div className="mt-4 sticky bottom-0 flex justify-end gap-2 border-t border-slate-700/70 bg-slate-900 pt-3">
              <button
                onClick={() => setIncidentTargetPassageId(null)}
                className="min-h-11 rounded border border-slate-600 px-4 py-2 text-sm text-slate-200"
              >
                Annuler
              </button>
              <button
                onClick={() => void submitIncident()}
                disabled={mutations.pending.reportIncident || incidentDescription.trim().length < 5}
                className="min-h-11 rounded border border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {mutations.pending.reportIncident ? "Envoi..." : "Envoyer incident"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {viewIncidentPassageId != null ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="flex max-h-[92vh] w-full max-w-xl flex-col rounded-2xl border border-slate-700 bg-slate-900 p-4 sm:p-5">
            <h3 className="text-lg font-semibold text-white">Incidents du passage #{viewIncidentPassageId}</h3>
            {incidentsForViewedPassage.length === 0 ? (
              <p className="mt-3 text-sm text-slate-300">Aucun incident signalé.</p>
            ) : (
              <div className="mt-3 space-y-2 overflow-y-auto pr-1">
                {incidentsForViewedPassage.map((it) => (
                  <div key={it.id} className="rounded-lg border border-slate-700 bg-slate-950/40 p-3 text-sm text-slate-200">
                    <p className="font-semibold">{it.type}</p>
                    <p className="text-slate-300">{it.description ?? "—"}</p>
                    <p className="mt-1 text-xs text-slate-400">
                      {new Date(it.createdAt).toLocaleString("fr-FR")} • {it.createdBy ? `${it.createdBy.prenom ?? ""} ${it.createdBy.nom}`.trim() : "Auteur inconnu"}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 sticky bottom-0 flex justify-end border-t border-slate-700/70 bg-slate-900 pt-3">
              <button onClick={() => setViewIncidentPassageId(null)} className="min-h-11 rounded border border-slate-600 px-4 py-2 text-sm text-slate-200">Fermer</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmCloseOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col rounded-2xl border border-slate-700 bg-slate-900 p-4 sm:p-5">
            <h3 className="text-lg font-semibold text-white">{closureModalTitle(closurePreview?.severity)}</h3>
            <p className="mt-1 text-xs text-slate-400">
              Niveau :{" "}
              <span className="font-semibold text-slate-200">
                {closurePreview?.severity ? SEVERITY_LABEL_FR[closurePreview.severity] ?? closurePreview.severity : "—"}
              </span>
            </p>

            {closurePreviewLoading ? (
              <p className="mt-4 text-sm text-slate-400">Analyse de clôture en cours…</p>
            ) : closurePreview ? (
              <>
                <div className="mt-3 rounded-lg border border-slate-700/80 bg-slate-950/50 px-3 py-2 text-sm text-slate-200">
                  <p>
                    Score qualité :{" "}
                    <span className="font-bold text-white">{closurePreview.qualityScore}</span>/100
                  </p>
                  <div className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
                    <span>Passages réalisés : {closurePreview.summary.realised}</span>
                    <span>Passages échoués : {closurePreview.summary.failed}</span>
                    <span>Restants : {closurePreview.summary.pending}</span>
                    <span>En cours : {closurePreview.summary.running}</span>
                    <span>Incidents ouverts : {closurePreview.summary.incidentsOpen}</span>
                    <span>GPS incomplets : {closurePreview.summary.gpsMissing + closurePreview.summary.gpsPartial}</span>
                    <span>Photos manquantes : {closurePreview.summary.photosMissing}</span>
                    <span>Validations en attente : {closurePreview.summary.validationsPending}</span>
                  </div>
                </div>

                {closurePreview.blockingReasons.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-red-600/40 bg-red-950/25 px-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-200">Raisons bloquantes</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-red-100">
                      {closurePreview.blockingReasons.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {closurePreview.warningReasons.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-amber-600/35 bg-amber-950/20 px-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-200">Avertissements</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-amber-100">
                      {closurePreview.warningReasons.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {closurePreview.infoReasons.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-cyan-600/30 bg-cyan-950/20 px-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-cyan-200">Informations</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-cyan-50">
                      {closurePreview.infoReasons.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {closurePreview.requiresReason ? (
                  <label className="mt-4 block text-sm text-slate-300">
                    Motif de clôture avec anomalies *
                    <textarea
                      value={closureReason}
                      onChange={(e) => setClosureReason(e.target.value)}
                      rows={4}
                      placeholder="Minimum 10 caractères — traçabilité superviseur."
                      className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
                    />
                    <span className="mt-1 block text-[11px] text-slate-500">
                      {closureReason.trim().length}/10 caractères minimum
                    </span>
                  </label>
                ) : null}

                <p className="mt-3 text-sm text-slate-400">
                  La clôture fige l&apos;exécution terrain et engage la responsabilité du superviseur.
                </p>
              </>
            ) : (
              <p className="mt-4 text-sm text-amber-200">Impossible de charger la prévisualisation. Réessayez.</p>
            )}

            <div className="mt-4 sticky bottom-0 flex flex-wrap justify-end gap-2 border-t border-slate-700/70 bg-slate-900 pt-3">
              <button
                type="button"
                onClick={() => setConfirmCloseOpen(false)}
                className="min-h-11 rounded border border-slate-600 px-4 py-2 text-sm text-slate-200"
              >
                Annuler
              </button>
              {closurePreview?.severity === "BLOCKING" || !closurePreview?.canClose ? (
                <button
                  type="button"
                  onClick={() => setConfirmCloseOpen(false)}
                  className="min-h-11 rounded border border-amber-500/50 px-4 py-2 text-sm font-semibold text-amber-100"
                >
                  Corriger avant clôture
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const ok =
                      closurePreview &&
                      closurePreview.canClose &&
                      (!closurePreview.requiresReason || closureReason.trim().length >= 10);
                    if (!ok) return;
                    setConfirmCloseOpen(false);
                    void mutations.completeTournee(closureReason.trim() || undefined);
                  }}
                  disabled={
                    mutations.pending.completeTournee ||
                    closurePreviewLoading ||
                    !closurePreview ||
                    !closurePreview.canClose ||
                    (closurePreview.requiresReason && closureReason.trim().length < 10)
                  }
                  className="min-h-11 rounded border border-blue-500/50 px-4 py-2 text-sm font-semibold text-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {mutations.pending.completeTournee ? "Clôture..." : "Confirmer clôture"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <ToastContainer theme="dark" position="top-right" />
    </div>
  );
}
